import { cn } from '../cn';

import type { ButtonHTMLAttributes, ReactNode } from 'react';

/**
 * `primary`, `neutral`, `danger` and `ghost` are the design system's filled and bordered
 * buttons and take `size`. `outline` is the white pill with a default border that the
 * product screens use for secondary actions (design `.ov-ghostbtn`: 13 px / 600, 7 × 14 px
 * padding, neutral hover). `link` is a text action in the info accent, underlined on hover
 * (design `.ov-linkbtn`). `outline` and `link` have one size each and ignore `size`.
 */
export type ButtonVariant = 'primary' | 'neutral' | 'danger' | 'ghost' | 'outline' | 'link';
export type ButtonSize = 'lg' | 'sm';

const VARIANT: Record<ButtonVariant, string> = {
  primary:
    'bg-surface-action-primary-default text-content-onactioninversed hover:bg-surface-action-primary-hover active:bg-surface-action-primary-active disabled:bg-surface-action-primary-disabled disabled:text-content-disabled',
  neutral:
    'bg-surface-action-neutral-default text-content-onactioninversed hover:bg-surface-action-neutral-hover active:bg-surface-action-neutral-active disabled:bg-surface-action-neutral-disabled disabled:text-content-disabled',
  danger:
    'bg-surface-action-danger-default text-content-onactioninversed hover:bg-surface-action-danger-hover active:bg-surface-action-danger-active disabled:bg-surface-action-danger-disabled disabled:text-content-disabled',
  ghost:
    'bg-surface-action-default-default text-content-onaction border border-border-default hover:bg-surface-action-default-hover active:bg-surface-action-default-active disabled:bg-surface-action-default-disabled disabled:text-content-disabled',
  outline:
    'gap-1.5 border border-border-default bg-surface-static-default px-3.5 py-[7px] text-[13px] font-semibold text-content-default hover:bg-surface-static-neutral-hover disabled:opacity-45',
  link: 'gap-1 border-none bg-transparent p-0 text-[13px] font-semibold text-surface-static-info-accented hover:underline disabled:opacity-45',
};

const SIZE: Record<ButtonSize, string> = {
  lg: 'ds-button-lg px-6 py-2.5',
  sm: 'ds-button-sm px-5 py-2',
};

/** Variants that set their own type and padding. */
const FIXED_SIZE: ReadonlySet<ButtonVariant> = new Set(['outline', 'link']);

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'type'> {
  variant?: ButtonVariant | undefined;
  /** For the filled and ghost variants. `outline` and `link` ignore it. */
  size?: ButtonSize | undefined;
  /** An `<Icon />`, placed before (`start`) or after (`end`) the label. */
  icon?: ReactNode | undefined;
  iconPosition?: 'start' | 'end' | undefined;
  type?: 'button' | 'submit' | 'reset' | undefined;
}

/** All interactive controls are fully rounded (design rule). */
export function Button({
  variant = 'primary',
  size = 'lg',
  icon,
  iconPosition = 'start',
  type = 'button',
  className,
  children,
  ...rest
}: ButtonProps) {
  return (
    <button
      type={type}
      className={cn(
        'inline-flex shrink-0 cursor-pointer items-center justify-center gap-2 rounded-full whitespace-nowrap transition-colors duration-150',
        'disabled:cursor-not-allowed',
        FIXED_SIZE.has(variant) ? null : SIZE[size],
        VARIANT[variant],
        className,
      )}
      {...rest}
    >
      {icon && iconPosition === 'start' ? icon : null}
      {children}
      {icon && iconPosition === 'end' ? icon : null}
    </button>
  );
}
